// ─────────────────────────────────────────────────────────────────────────────
// Comptage de segments SMS — MIROIR EXACT du backend App\Services\SmsService.
//
// Twilio (comme tous les opérateurs) facture PAR SEGMENT, pas par « SMS » :
//   • GSM-7 (texte simple, accents é è à ù ì ò ok) : 160 car / segment
//     (153 dès qu'il y a plusieurs segments, à cause de l'en-tête de concaténation).
//   • Unicode (dès UN emoji ou un accent rare ê î ô ë ï ç, tiret long…) : la limite
//     TOMBE à 70 car / segment (67 en multi-segment). Un emoji astral (🥸) compte
//     pour 2 unités UTF-16, exactement comme le mesure l'opérateur.
//
// → 1 segment = 1 crédit. Cette fonction dit le VRAI coût d'un message.
// ─────────────────────────────────────────────────────────────────────────────

const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡" +
  "ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXT = "^{}\\[~]|€\f";

/**
 * @param {string} text
 * @returns {{ chars: number, segments: number, unicode: boolean }}
 */
export function smsInfo(text) {
  const str = text || '';
  const cps = Array.from(str); // par point de code (un emoji = 1 élément pour l'affichage)

  let gsm = true;
  let gsmUnits = 0;
  for (const ch of cps) {
    if (GSM7_BASIC.indexOf(ch) !== -1) gsmUnits += 1;
    else if (GSM7_EXT.indexOf(ch) !== -1) gsmUnits += 2;
    else { gsm = false; break; }
  }

  let segments;
  if (gsm) {
    segments = gsmUnits <= 160 ? 1 : Math.ceil(gsmUnits / 153);
  } else {
    const units16 = str.length; // unités UTF-16 (emoji astral = 2) — comme l'opérateur
    segments = units16 <= 70 ? 1 : Math.ceil(units16 / 67);
  }

  return { chars: cps.length, segments: Math.max(1, segments), unicode: !gsm };
}

/** Nombre de segments (= crédits) consommés par un message. */
export function smsSegments(text) {
  return smsInfo(text).segments;
}

/**
 * Longueur d'un opt-out « Stop » ajouté par le backend aux SMS de campagne/contact
 * connu (\nStop: https://api.beeappee.com/api/s/<id>/<token>). ~56 caractères.
 * Sert à estimer le coût réel côté composer marketing.
 */
export const SMS_OPTOUT_LEN = 56;

/**
 * Dérive un SMS court et propre à partir du corps d'un e-mail.
 * - retire le HTML et les espaces superflus
 * - ANTI double-prénom : enlève une salutation initiale (« Bonjour {{prénom}}, »)
 *   car le SMS repréfixe « {{prénom}}, » lui-même
 * - retire les formules de politesse finales
 * - coupe à une PHRASE complète (jamais au milieu d'un mot) si trop long
 */
export function smsFromEmailBody(body) {
  if (!body) return '';
  let t = body
    .replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ').trim();
  t = t.replace(/^\s*(bonjour|coucou|hello|salut)?\s*(\{\{\s*pr[ée]nom\s*\}\})\s*[,!.]?\s*/i, '')
       .replace(/^\s*bonjour[^,\n.!]*[,.!]?\s*/i, '')
       .trim();
  t = t.replace(/(à très vite\s*!?|bien cordialement.*|cordialement.*|belle f[êe]te.*|l['’]équipe.*)$/i, '').trim();
  let core = t;
  if (core.length > 130) {
    const cut = core.slice(0, 130);
    const lastPunct = Math.max(cut.lastIndexOf('.'), cut.lastIndexOf('!'), cut.lastIndexOf('?'), cut.lastIndexOf('…'));
    core = lastPunct > 40
      ? cut.slice(0, lastPunct + 1).trim()
      : cut.replace(/\s+\S*$/, '').trim() + '…';
  }
  return `{{prénom}}, ${core} — {{entreprise}}`.replace(/\s+/g, ' ').trim();
}
